import { localizeDateNames } from "@/lib/dateNames";
import { useTranslation } from "react-i18next";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatDateCompact } from "@/lib/formatDate";
import { useApp } from "@/context/AppContext";
import { useRestaurantScope } from "@/hooks/useRestaurantScope";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Copy, X, Check, Lock, Sparkles, Printer, ChevronDown, AlertTriangle, Eye, Search } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { toast } from "sonner";
import DailyPrepList from "@/pages/DailyPrepList";
import PrintMenuDialog from "@/pages/PrintMenuDialog";
import { groupPurchaseNeeds } from "@/lib/purchaseGrouping";

const DAY_STATUS_LABEL = {
    not_planned: "Not Planned",
    in_progress: "In Progress",
    ready: "Ready",
    closed: "Closed",
};
const DAY_STATUS_TONE = {
    not_planned: "action_required",
    in_progress: "pending",
    ready: "completed",
    closed: "completed",
};

// Operational display order inside each day card and in the View Menu modal.
// Purely presentational — the persisted `weekly_menu_dishes` order is NOT
// mutated, so Prep List, Purchase Needs, Suggested Order and Production
// Review continue reading the same underlying data.
const CATEGORY_GROUP_ORDER = {
    Vegetarian: 1,
    Vegan: 1,
    "Fish & Seafood": 2,
    Meat: 3,
    Poultry: 3,
};

function sortDishesByCategory(dishes, recipes) {
    if (!Array.isArray(dishes) || dishes.length === 0) return [];
    const byId = new Map((recipes || []).map((r) => [r.id, r]));
    const decorated = dishes.map((d, idx) => {
        const r = byId.get(d.recipe_id);
        const cat = (r?.category || "").trim();
        const group = CATEGORY_GROUP_ORDER[cat] ?? 9;
        return { dish: d, category: cat, group, idx };
    });
    decorated.sort((a, b) => {
        if (a.group !== b.group) return a.group - b.group;
        // Within the same operational group, sort by category name for
        // predictable, stable output ("Meat" before "Poultry" etc.).
        if (a.category !== b.category) {
            return a.category.localeCompare(b.category, undefined, { sensitivity: "base" });
        }
        // Preserve insertion order within the same category.
        return a.idx - b.idx;
    });
    return decorated.map((e) => ({ ...e.dish, _category: e.category }));
}

export default function WeeklyMenu() {
    const { t } = useTranslation();
    const { restaurantId, user } = useApp();
    const { scopedParams } = useRestaurantScope();
    // R1.5 · weekOffset (weeks from current Monday): 0 = current,
    // 1 = next, negative = historical (strictly read-only).
    const [weekOffset, setWeekOffset] = useState(0);
    const [menu, setMenu] = useState(null);
    const [recipes, setRecipes] = useState([]);
    const [needs, setNeeds] = useState([]);
    const [addOpen, setAddOpen] = useState(false);
    const [activeDay, setActiveDay] = useState(null);
    const [copyOpen, setCopyOpen] = useState(false);
    const [copySource, setCopySource] = useState(null);
    const [prepDay, setPrepDay] = useState(null);
    const [printMode, setPrintMode] = useState(null);   // day_menu | week_menu | prep
    const [selectedDay, setSelectedDay] = useState(null);
    const [viewDay, setViewDay] = useState(null);

    const isHistorical = weekOffset < 0;

    const loadAll = useCallback(async () => {
        if (!restaurantId) return;
        // R1.5/R1.6 · always pass `offset`. Backend treats offset<0 as
        // strictly read-only and never inserts historical records.
        const params = { params: { offset: weekOffset, ...scopedParams } };
        const { data } = await api.get("/weekly-menus/by-week", params);
        setMenu(data);
        const rp = { params: { ...scopedParams } };
        const { data: recs } = await api.get("/recipes", rp);
        setRecipes(recs);
        // Purchase needs are only meaningful for a persisted menu.
        // Historical stubs (`id === null`) and historical read-only
        // menus should not trigger a purchase-needs fetch.
        if (data && data.id && !isHistorical) {
            try {
                const { data: pn } = await api.get(`/purchase-needs/${data.id}`);
                const grouped = await groupPurchaseNeeds(pn);
                setNeeds(grouped);
            } catch { setNeeds([]); }
        } else {
            setNeeds([]);
        }
    }, [restaurantId, weekOffset, isHistorical, scopedParams]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const openAddDish = (day) => { setActiveDay(day); setAddOpen(true); };

    const removeDish = async (dishId) => {
        await api.delete(`/weekly-menus/dishes/${dishId}`);
        loadAll();
    };

    const setDayStatus = async (day, status) => {
        await api.patch(`/weekly-menus/days/${day.id}/status`, { status });
        loadAll();
    };

    const onCopyPreviousWeek = async () => {
        const res = await api.post("/weekly-menus/copy-previous-week", { to_menu_id: menu.id });
        toast.success(`Copied ${res.data.copied} dishes from previous week`);
        loadAll();
    };

    const onCopyDay = async (toDayId) => {
        const res = await api.post("/weekly-menus/copy-day", {
            from_day_id: copySource.id, to_day_id: toDayId,
        });
        toast.success(`Copied ${res.data.copied} dishes`);
        setCopyOpen(false); setCopySource(null);
        loadAll();
    };

    const weekRangeLabel = useMemo(() => {
        if (!menu) return "";
        const s = new Date(menu.week_start_date);
        const e = new Date(menu.week_end_date);
        const f = (d) => localizeDateNames(d.toLocaleDateString("en-US", { month: "short", day: "numeric" }));
        return `${f(s)} — ${f(e)}`;
    }, [menu]);

    return (
        <div className="px-6 lg:px-12 py-10 max-w-7xl mx-auto">
            <PageHeader
                title={t("weeklyMenu.weeklyMenu", "Weekly Menu")}
                subtitle={`${t("weeklyMenu.whatAreWeServing", "What are we serving?")} · ${weekRangeLabel}`}
                context={weekRangeLabel}
                action={
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="week-previous"
                            onClick={() => setWeekOffset((o) => o - 1)}
                            className="px-3 h-10 text-sm font-semibold rounded-md border border-neutral-200 hover:bg-neutral-50 text-neutral-700"
                            title={t("weeklyMenu.viewThePreviousWeekReadOnly", "View the previous week (read-only)")}
                        >{t("weeklyMenu.previousWeek", "‹ Previous Week")}</button>
                        <div className="inline-flex rounded-md border border-neutral-200 overflow-hidden">
                            <button
                                data-testid="week-current"
                                onClick={() => setWeekOffset(0)}
                                className={`px-4 h-10 text-sm font-semibold ${weekOffset === 0 ? "bg-neutral-900 text-white" : "bg-white text-neutral-700"}`}
                            >{t("weeklyMenu.currentWeek", "Current Week")}</button>
                            <button
                                data-testid="week-next"
                                onClick={() => setWeekOffset(1)}
                                className={`px-4 h-10 text-sm font-semibold ${weekOffset === 1 ? "bg-neutral-900 text-white" : "bg-white text-neutral-700"}`}
                            >{t("weeklyMenu.nextWeek", "Next Week")}</button>
                        </div>
                        {!isHistorical && (
                            <button
                                data-testid="copy-prev-week"
                                onClick={onCopyPreviousWeek}
                                className="h-10 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-200 hover:bg-neutral-50 text-sm font-semibold"
                            >
                                <Copy className="h-4 w-4" /> {t("weeklyMenu.copyPreviousWeek", "Copy previous week")}
                            </button>
                        )}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    data-testid="weekly-menu-print-btn"
                                    className="h-10 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-200 hover:bg-neutral-50 text-sm font-semibold"
                                >
                                    <Printer className="h-4 w-4" /> {t("weeklyMenu.print", "Print")}
                                    <ChevronDown className="h-3 w-3 opacity-60" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem
                                    data-testid="print-option-day-menu"
                                    onSelect={() => setPrintMode("day_menu")}>
                                    {t("weeklyMenu.dayMenu", "Day Menu")}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    data-testid="print-option-week-menu"
                                    onSelect={() => setPrintMode("week_menu")}>
                                    {t("weeklyMenu.weeklyMenu", "Weekly Menu")}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    data-testid="print-option-prep"
                                    onSelect={() => setPrintMode("prep")}>
                                    Daily Prep Lists…
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                }
            />

            {/* R1.5 · historical banner — makes the read-only state obvious. */}
            {isHistorical && (
                <div
                    data-testid="weekly-menu-historical-banner"
                    className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900"
                >
                    {t("weeklyMenu.historicalWeekReadOnly", "Viewing a historical week — read-only. Dishes cannot be added, edited, deleted, or copied into this week.")}
                </div>
            )}

            {/* R1.5 · historical stub — no menu exists for this Monday. */}
            {isHistorical && menu && !menu.id && (
                <div
                    data-testid="weekly-menu-historical-empty"
                    className="kop-card p-6 text-sm text-neutral-500"
                >
                    {t("weeklyMenu.noMenuWasPlannedForThis", "No menu was planned for this week.")}
                </div>
            )}

            {/* Day grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {menu?.days?.map((day) => (
                    <div key={day.id} className="kop-card p-5 flex flex-col" data-testid={`day-card-${day.day_name.toLowerCase()}`}>
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="font-display text-xl font-bold">{t(`days.${(day?.day_name || "").toLowerCase()}`, day?.day_name || "")}</div>
                                <div className="text-xs text-neutral-500">
                                    {formatDateCompact(day.date)}
                                </div>
                            </div>
                            <StatusBadge status={DAY_STATUS_TONE[day.status]}>
                                {t(`weeklyMenu.dayStatus.${day.status}`, DAY_STATUS_LABEL[day.status] || "")}
                            </StatusBadge>
                        </div>
                        <div className="mt-4 space-y-2 flex-1 min-h-[80px]">
                            {(day.dishes || []).length === 0 ? (
                                <div className="text-xs text-neutral-400 italic">{t("weeklyMenu.noDishesPlanned", "No dishes planned.")}</div>
                            ) : (
                                sortDishesByCategory(day.dishes, recipes).map((d) => (
                                    <div key={d.id} className="flex items-start gap-2 p-2 rounded-md bg-neutral-50">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold truncate">{d.dish_name}</div>
                                            <div className="text-xs text-neutral-500">
                                                {d._category && (
                                                    <span className="mr-1.5">{d._category} ·</span>
                                                )}
                                                {t("weeklyMenu.paxCount", { defaultValue: "{{count}} pax", count: d.planned_pax })}
                                            </div>
                                        </div>
                                        {!isHistorical && (
                                            <button
                                                data-testid={`remove-dish-${d.id}`}
                                                onClick={() => removeDish(d.id)}
                                                className="h-7 w-7 grid place-items-center rounded hover:bg-neutral-200"
                                                title={t("weeklyMenu.removeDish", "Remove dish")}
                                            >
                                                <X className="h-3.5 w-3.5 text-neutral-500" />
                                            </button>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="mt-4 pt-4 border-t border-neutral-100 flex items-center gap-2 flex-wrap">
                            {!isHistorical && (
                                <button
                                    data-testid={`add-dish-${day.day_name.toLowerCase()}`}
                                    onClick={() => openAddDish(day)}
                                    className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-neutral-900 text-white text-xs font-semibold hover:bg-neutral-800"
                                >
                                    <Plus className="h-3.5 w-3.5" /> {t("weeklyMenu.addDish", "Add Dish")}
                                </button>
                            )}
                            {!isHistorical && (
                                <button
                                    data-testid={`copy-day-${day.day_name.toLowerCase()}`}
                                    onClick={() => { setCopySource(day); setCopyOpen(true); }}
                                    className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-neutral-200 text-xs font-semibold hover:bg-neutral-50"
                                    title={t("weeklyMenu.copyDay", "Copy day")}
                                >
                                    <Copy className="h-3.5 w-3.5" /> {t("weeklyMenu.copy", "Copy")}
                                </button>
                            )}
                            {!isHistorical && (
                                <button
                                    data-testid={`prep-list-${day.day_name.toLowerCase()}`}
                                    onClick={() => setPrepDay(day)}
                                    className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-neutral-200 text-xs font-semibold hover:bg-neutral-50"
                                    title={t("weeklyMenu.dailyPrepListAi", "Daily Prep List (AI)")}
                                >
                                    <Sparkles className="h-3.5 w-3.5" /> {t("weeklyMenu.prepList", "Prep list")}
                                </button>
                            )}
                            <button
                                data-testid={`view-menu-${day.day_name.toLowerCase()}`}
                                onClick={() => setViewDay(day)}
                                className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-neutral-200 text-xs font-semibold hover:bg-neutral-50"
                                title={t("weeklyMenu.viewDayMenu", "View day menu")}
                            >
                                <Eye className="h-3.5 w-3.5" /> {t("weeklyMenu.view", "View")}
                            </button>
                            {!isHistorical && day.status !== "ready" && day.status !== "closed" && (
                                <button
                                    data-testid={`mark-ready-${day.day_name.toLowerCase()}`}
                                    onClick={() => setDayStatus(day, "ready")}
                                    className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-50"
                                >
                                    <Check className="h-3.5 w-3.5" /> {t("weeklyMenu.ready", "Ready")}
                                </button>
                            )}
                            {!isHistorical && day.status !== "closed" && (
                                <button
                                    data-testid={`mark-closed-${day.day_name.toLowerCase()}`}
                                    onClick={() => setDayStatus(day, "closed")}
                                    className="h-9 px-2 inline-flex items-center gap-1 rounded-md border border-neutral-200 text-neutral-500 text-xs font-semibold hover:bg-neutral-50"
                                    title={t("weeklyMenu.closeDay", "Close day")}
                                >
                                    <Lock className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Purchase needs */}
            <section className="mt-12">
                <h2 className="font-display text-2xl font-bold mb-1">{t("weeklyMenu.purchaseNeeds", "Purchase Needs")}</h2>
                <p className="text-sm text-neutral-500 mb-4">
                    {t("weeklyMenu.consolidatedIngredientQuantitiesForThisWeek", "Consolidated ingredient quantities for this week, compared with current inventory.")}
                    {" "}
                    {t("weeklyMenu.suggestedOrderQuantitiesOnlyNoPrices", "Suggested order quantities only — no prices.")}
                </p>
                {needs.length === 0 ? (
                    <div className="kop-card p-6 text-sm text-neutral-500">
                        {t("weeklyMenu.noIngredientsRequiredYetPlanSome", "No ingredients required yet. Plan some dishes to see needs.")}
                    </div>
                ) : (
                    <div className="kop-card overflow-hidden">
                        <table className="w-full text-sm" data-testid="purchase-needs-table">
                            <thead className="bg-neutral-50">
                                <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                                    <th className="px-5 py-3 font-semibold">{t("weeklyMenu.ingredient", "Ingredient")}</th>
                                    <th className="px-5 py-3 font-semibold">{t("weeklyMenu.required", "Required")}</th>
                                    <th className="px-5 py-3 font-semibold">{t("weeklyMenu.available", "Available")}</th>
                                    <th className="px-5 py-3 font-semibold">{t("weeklyMenu.suggestedOrder", "Suggested order")}</th>
                                    <th className="px-5 py-3 font-semibold">{t("common.status", "Status")}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-100">
                                {needs.map((n, i) => (
                                    <tr key={i} data-testid={`need-row-${i}`}>
                                        <td className="px-5 py-3 font-medium">
                                            <span>{n.ingredient_name}</span>
                                            {n._sources && n._sources.length > 1 && (
                                                <span
                                                    data-testid={`need-merged-${i}`}
                                                    title={`${t("weeklyMenu.combinedFrom", "Combined from:")} ${n._sources.map((s) => `${s.ingredient_name || ""} (${s.quantity ?? s.allocated_coverage ?? 0} ${s.unit})`).join(" + ")}`}
                                                    className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-neutral-100 text-neutral-600 border border-neutral-200"
                                                >
                                                    ×{n._sources.length}
                                                </span>
                                            )}
                                            {n._mixed_units && (
                                                <span
                                                    data-testid={`need-mixed-${i}`}
                                                    title={t("weeklyMenu.sameIngredientDifferentUnitsKeptSeparate", "Same ingredient, different units — kept separate for safety. Chef, please review.")}
                                                    className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                                                >
                                                    <AlertTriangle className="h-3 w-3" />
                                                    {t("weeklyMenu.mixedUnits", "Mixed units")}
                                                </span>
                                            )}
                                            {n.suggested_order_status === "provisional" && (
                                                <span
                                                    data-testid={`need-provisional-${i}`}
                                                    title={t("weeklyMenu.recognitionAllocationDidNotCompleteThe", "Recognition / allocation did not complete. The purchase remainder may shrink on the next refresh.")}
                                                    className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                                                >
                                                    {t("weeklyMenu.provisional", "Provisional")}
                                                </span>
                                            )}
                                            {n.recognition_status === "pending" && (
                                                <span
                                                    data-testid={`need-check-${i}`}
                                                    title={t("weeklyMenu.compatibleAlternativesDetectedReviewInThe", "Compatible alternatives detected — review in the Suggested order popover.")}
                                                    className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200"
                                                >
                                                    {t("weeklyMenu.needsCheck", "Needs check")}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-neutral-700">{n.total_quantity} {n.unit}</td>
                                        <td className="px-5 py-3 text-neutral-700">{n.available ?? 0} {n.unit}</td>
                                        <td className="px-5 py-3 font-semibold">
                                            {Array.isArray(n.compatible_sources) && n.compatible_sources.length > 0 ? (
                                                <Popover>
                                                    <PopoverTrigger asChild>
                                                        <button
                                                            data-testid={`suggested-order-popover-${i}`}
                                                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border ${n.suggested_order > 0 ? "border-amber-200 bg-amber-50 text-amber-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}
                                                        >
                                                            {n.suggested_order} {n.unit}
                                                        </button>
                                                    </PopoverTrigger>
                                                    <PopoverContent
                                                        data-testid={`suggested-order-popover-content-${i}`}
                                                        className="w-80 p-3 text-xs"
                                                    >
                                                        <div className="font-semibold text-neutral-900 mb-1">
                                                            {t("weeklyMenu.coverageDetail", "Coverage detail")}
                                                        </div>
                                                        {n.suggested_order_status === "provisional" && (
                                                            <div className="mb-1 text-amber-700">
                                                                {t("weeklyMenu.provisionalRefreshAfterRecognitionCompletes", "Provisional — refresh after recognition completes.")}
                                                            </div>
                                                        )}
                                                        <ul className="space-y-1 mb-2">
                                                            {n.compatible_sources.map((cs) => (
                                                                <li key={cs.product_id} className="flex items-center justify-between">
                                                                    <span className="truncate pr-2">
                                                                        {cs.product_name}
                                                                    </span>
                                                                    <span className="text-neutral-500">
                                                                        {cs.allocated_qty} {cs.unit}
                                                                    </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                        {Array.isArray(n._sources) && n._sources.length > 0 && (
                                                            <div className="mt-2 border-t border-neutral-100 pt-2">
                                                                <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">
                                                                    {t("weeklyMenu.perRecipeIngredient", "Per recipe ingredient")}
                                                                </div>
                                                                <ul className="space-y-1">
                                                                    {n._sources.map((s) => (
                                                                        <li
                                                                            key={s.recipe_ingredient_id || s.ingredient_name}
                                                                            className="flex items-center justify-between gap-2"
                                                                        >
                                                                            <span className="truncate">
                                                                                {s.ingredient_name}
                                                                            </span>
                                                                            <span className="text-neutral-500">
                                                                                need {s.shortfall ?? 0} {s.unit}
                                                                            </span>
                                                                            {Array.isArray(n.compatible_sources) && n.compatible_sources.length > 0 && (
                                                                                <span className="flex items-center gap-1">
                                                                                    {n.compatible_sources.map((cs) => (
                                                                                        <span key={cs.product_id} className="flex items-center gap-0.5">
                                                                                            <button
                                                                                                data-testid={`decision-confirm-${s.recipe_ingredient_id}-${cs.product_id}`}
                                                                                                className="text-emerald-700 hover:underline"
                                                                                                onClick={async () => {
                                                                                                    try {
                                                                                                        await api.post("/purchase-needs-decisions", {
                                                                                                            recipe_ingredient_id: s.recipe_ingredient_id,
                                                                                                            product_id: cs.product_id,
                                                                                                            direction: "confirm",
                                                                                                        });
                                                                                                        toast.success(t("weeklyMenu.useForThisRequirement", "Use for this requirement"));
                                                                                                        loadAll();
                                                                                                    } catch (e) {
                                                                                                        toast.error((e?.response?.data?.detail != null ? apiErrorMessage(e) : t("weeklyMenu.couldNotRecordDecision", "Could not record decision")));
                                                                                                    }
                                                                                                }}
                                                                                                title={t("weeklyMenu.useForThisRequirement", "Use for this requirement")}
                                                                                            >
                                                                                                {t("weeklyMenu.use", "Use")}
                                                                                            </button>
                                                                                            <span className="text-neutral-300">·</span>
                                                                                            <button
                                                                                                data-testid={`decision-reject-${s.recipe_ingredient_id}-${cs.product_id}`}
                                                                                                className="text-neutral-500 hover:underline"
                                                                                                onClick={async () => {
                                                                                                    try {
                                                                                                        await api.post("/purchase-needs-decisions", {
                                                                                                            recipe_ingredient_id: s.recipe_ingredient_id,
                                                                                                            product_id: cs.product_id,
                                                                                                            direction: "reject",
                                                                                                        });
                                                                                                        toast.success(t("weeklyMenu.notThisOne", "Not this one"));
                                                                                                        loadAll();
                                                                                                    } catch (e) {
                                                                                                        toast.error((e?.response?.data?.detail != null ? apiErrorMessage(e) : t("weeklyMenu.couldNotRecordDecision", "Could not record decision")));
                                                                                                    }
                                                                                                }}
                                                                                                title={t("weeklyMenu.notThisOne", "Not this one")}
                                                                                            >
                                                                                                {t("weeklyMenu.notThisOne", "Not this one")}
                                                                                            </button>
                                                                                        </span>
                                                                                    ))}
                                                                                </span>
                                                                            )}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </PopoverContent>
                                                </Popover>
                                            ) : (
                                                n.suggested_order > 0
                                                    ? <span className="text-amber-700">{n.suggested_order} {n.unit}</span>
                                                    : <span className="text-emerald-700">0 {n.unit}</span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3">
                                            {n.stock_status === "ready" && <StatusBadge status="completed">{t("weeklyMenu.ready", "Ready")}</StatusBadge>}
                                            {n.stock_status === "low_stock" && <StatusBadge status="pending">{t("weeklyMenu.lowStock", "Low stock")}</StatusBadge>}
                                            {n.stock_status === "out_of_stock" && <StatusBadge status="action_required">{t("weeklyMenu.outOfStock", "Out of stock")}</StatusBadge>}
                                            {n.stock_status === "unknown" && <StatusBadge status="pending">{t("weeklyMenu.unknown", "Unknown")}</StatusBadge>}
                                            {/* Backwards compat: fall back to legacy label if new field absent */}
                                            {!n.stock_status && n.availability === "available" && <StatusBadge status="completed">{t("weeklyMenu.available", "Available")}</StatusBadge>}
                                            {!n.stock_status && n.availability === "pending_delivery" && <StatusBadge status="pending">{t("weeklyMenu.pendingDelivery", "Pending Delivery")}</StatusBadge>}
                                            {!n.stock_status && n.availability === "missing" && <StatusBadge status="action_required">{t("weeklyMenu.missing", "Missing")}</StatusBadge>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {/* Add dish dialog */}
            <AddDishDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                day={activeDay}
                recipes={recipes}
                onSaved={() => { setAddOpen(false); loadAll(); }}
                menuId={menu?.id}
            />

            {/* Copy day dialog */}
            <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t("weeklyMenu.copyDayTitle", { defaultValue: "Copy {{day}}", day: t(`days.${(copySource?.day_name || "").toLowerCase()}`, copySource?.day_name || "") })}</DialogTitle>
                        <DialogDescription>{t("weeklyMenu.selectTheDayToCopyDishes", "Select the day to copy dishes into.")}</DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-2 my-2">
                        {(menu?.days || []).filter((d) => d.id !== copySource?.id).map((d) => (
                            <button
                                key={d.id}
                                data-testid={`copy-target-${d.day_name.toLowerCase()}`}
                                onClick={() => onCopyDay(d.id)}
                                className="h-12 rounded-md border border-neutral-200 hover:border-neutral-900 text-sm font-semibold"
                            >
                                {t(`days.${(d?.day_name || "").toLowerCase()}`, d?.day_name || "")}
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Daily Prep List (AI) — belongs to Weekly Menu per Prompt 26 */}
            {prepDay && (
                <DailyPrepList
                    day={prepDay}
                    weeklyMenuId={menu?.id}
                    onClose={() => setPrepDay(null)}
                />
            )}

            {/* Print dialog — Day/Week × Menu/Prep list */}
            {printMode && menu && (
                <PrintMenuDialog
                    mode={printMode}
                    menu={menu}
                    days={menu.days || []}
                    selectedDay={selectedDay}
                    onClose={() => { setPrintMode(null); setSelectedDay(null); }}
                />
            )}

            {/* View Menu (read-only) — day-scoped review modal */}
            <ViewMenuDialog
                day={viewDay}
                recipes={recipes}
                onClose={() => setViewDay(null)}
            />
        </div>
    );
}

function AddDishDialog({ open, onClose, day, recipes, onSaved, menuId }) {
    const { t } = useTranslation();
    const [recipeId, setRecipeId] = useState("");
    const [pax, setPax] = useState(20);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);

    useEffect(() => {
        if (open) { setRecipeId(""); setPax(20); setNotes(""); setPickerOpen(false); }
    }, [open]);

    const selectedRecipe = useMemo(
        () => recipes.find((r) => r.id === recipeId) || null,
        [recipes, recipeId],
    );

    const save = async () => {
        if (!recipeId) { toast.error(t("weeklyMenu.pickARecipe", "Pick a recipe")); return; }
        setSaving(true);
        const recipe = recipes.find((r) => r.id === recipeId);
        try {
            await api.post(`/weekly-menus/${menuId}/dishes`, {
                weekly_menu_day_id: day.id,
                recipe_id: recipeId,
                dish_name: recipe?.name || "Dish",
                planned_pax: Number(pax),
                notes,
            });
            toast.success(t("weeklyMenu.dishAdded", "Dish added"));
            onSaved();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="add-dish-dialog">
                <DialogHeader>
                    <DialogTitle>{t("weeklyMenu.addDishTitle", { defaultValue: "Add Dish — {{day}}", day: t(`days.${(day?.day_name || "").toLowerCase()}`, day?.day_name || "") })}</DialogTitle>
                    <DialogDescription>{t("weeklyMenu.pickARecipeAndEnterPlanned", "Pick a recipe and enter planned pax.")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("weeklyMenu.recipe", "Recipe")}</label>
                        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                            <PopoverTrigger asChild>
                                <button
                                    type="button"
                                    data-testid="add-dish-recipe"
                                    aria-expanded={pickerOpen}
                                    onClick={() => setPickerOpen((v) => !v)}
                                    className="mt-1 w-full h-11 px-3 rounded-md border border-neutral-300 bg-white text-left flex items-center justify-between focus:outline-none focus:border-neutral-900"
                                >
                                    {selectedRecipe ? (
                                        <span className="truncate">
                                            <span className="font-medium">{selectedRecipe.name}</span>
                                            <span className="text-neutral-400 ml-1">· {selectedRecipe.category}</span>
                                        </span>
                                    ) : (
                                        <span className="text-neutral-400 inline-flex items-center gap-2">
                                            <Search className="h-4 w-4" /> {t("weeklyMenu.searchRecipes", "Search recipes…")}
                                        </span>
                                    )}
                                    <ChevronDown className="h-4 w-4 opacity-60 ml-2 shrink-0" />
                                </button>
                            </PopoverTrigger>
                            <PopoverContent
                                className="p-0 w-[--radix-popover-trigger-width]"
                                align="start"
                                data-testid="recipe-picker-popover"
                            >
                                <Command
                                    filter={(value, search) => {
                                        // cmdk lowercases both sides; we still normalise
                                        // to make partial-word matching predictable.
                                        const v = (value || "").toLowerCase();
                                        const s = (search || "").toLowerCase().trim();
                                        if (!s) return 1;
                                        return v.includes(s) ? 1 : 0;
                                    }}
                                >
                                    <CommandInput
                                        placeholder={t("weeklyMenu.searchRecipes", "Search recipes…")}
                                        data-testid="recipe-picker-input"
                                    />
                                    <CommandList>
                                        <CommandEmpty>{t("weeklyMenu.noRecipesMatch", "No recipes match.")}</CommandEmpty>
                                        <CommandGroup>
                                            {recipes.map((r) => (
                                                <CommandItem
                                                    key={r.id}
                                                    // include category in `value` so a chef
                                                    // typing "vege" surfaces vegetarian recipes.
                                                    value={`${r.name} ${r.category || ""}`}
                                                    onSelect={() => {
                                                        setRecipeId(r.id);
                                                        setPickerOpen(false);
                                                    }}
                                                    data-testid={`recipe-option-${r.id}`}
                                                    className="cursor-pointer"
                                                >
                                                    <span className="truncate">
                                                        <span className="font-medium">{r.name}</span>
                                                        <span className="text-neutral-400 ml-1">· {r.category}</span>
                                                    </span>
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("weeklyMenu.plannedPax", "Planned pax")}</label>
                        <input
                            data-testid="add-dish-pax"
                            type="number"
                            min={1}
                            value={pax}
                            onChange={(e) => setPax(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("weeklyMenu.notesOptional", "Notes (optional)")}</label>
                        <textarea
                            data-testid="add-dish-notes"
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:border-neutral-900"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <button data-testid="dialog-cancel-button" onClick={onClose} className="h-10 px-4 rounded-md border border-neutral-200 text-sm font-semibold">{t("common.cancel", "Cancel")}</button>
                    <button
                        data-testid="add-dish-save"
                        onClick={save} disabled={saving}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60"
                    >
                        {saving ? t("common.saving", "Saving…") : t("weeklyMenu.saveDish", "Save dish")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function ViewMenuDialog({ day, recipes, onClose }) {
    const { t } = useTranslation();
    const open = !!day;
    const rows = useMemo(() => {
        if (!day) return [];
        const byId = new Map((recipes || []).map((r) => [r.id, r]));
        return sortDishesByCategory(day.dishes || [], recipes).map((d) => {
            const r = byId.get(d.recipe_id);
            return {
                id: d.id,
                dish_name: d.dish_name,
                planned_pax: d.planned_pax,
                recipe_name: r?.name || d.dish_name,
                category: r?.category || d._category || "",
                description: r?.description || "",
            };
        });
    }, [day, recipes]);

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent data-testid="view-menu-dialog" className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        {t(`days.${(day?.day_name || "").toLowerCase()}`, day?.day_name || "")}
                        {day?.date && (
                            <span className="text-neutral-400 font-normal ml-2">
                                · {formatDateCompact(day.date)}
                            </span>
                        )}
                    </DialogTitle>
                    <DialogDescription>{t("weeklyMenu.readOnlyViewOfThePlanned", "Read-only view of the planned dishes for this day.")}</DialogDescription>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto">
                    {rows.length === 0 ? (
                        <div className="py-8 text-center text-sm text-neutral-500 italic">
                            {t("weeklyMenu.noDishesPlannedForThisDay", "No dishes planned for this day.")}
                        </div>
                    ) : (
                        <ul className="divide-y divide-neutral-100">
                            {rows.map((r) => (
                                <li key={r.id} className="py-3" data-testid={`view-menu-row-${r.id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="text-base font-semibold text-neutral-900 truncate">
                                                {r.recipe_name}
                                            </div>
                                            {r.category && (
                                                <div className="text-xs uppercase tracking-wide text-neutral-500 mt-0.5">
                                                    {r.category}
                                                </div>
                                            )}
                                            {r.description && (
                                                <p className="text-sm text-neutral-600 mt-1.5 whitespace-pre-wrap">
                                                    {r.description}
                                                </p>
                                            )}
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <div className="text-sm font-semibold text-neutral-900">
                                                {r.planned_pax} pax
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <DialogFooter>
                    <button
                        data-testid="view-menu-close"
                        onClick={onClose}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
                    >
                        {t("common.close", "Close")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
